import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LiveChatWidget = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'Support Bot',
      message: 'Hello! How can we help you today?',
      timestamp: new Date()?.toLocaleTimeString(),
      isAgent: true,
      isBot: true
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (!message?.trim()) return;

    const newMessage = {
      id: messages?.length + 1,
      sender: 'You',
      message: message,
      timestamp: new Date()?.toLocaleTimeString(),
      isAgent: false
    };

    setMessages([...messages, newMessage]);
    setMessage('');

    // Simulate agent typing
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const agentResponse = {
        id: messages?.length + 2,
        sender: 'Sarah Johnson',
        message: 'Thank you for your message. Let me help you with that.',
        timestamp: new Date()?.toLocaleTimeString(),
        isAgent: true
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 2000);
  };

  const quickReplies = [
    'I need help with payments',
    'Form not working',
    'Billing question',
    'Technical issue'
  ];

  return (
    <div className="fixed bottom-4 right-4 z-50 w-full max-w-md">
      <div className={`rounded-lg shadow-2xl overflow-hidden border ${
        isDarkMode ? 'bg-[#0a0f0d] border-[#1a2820]' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`p-4 flex items-center justify-between ${
          isDarkMode ? 'bg-[#10b981]' : 'bg-[#107570]'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Icon name="MessageCircle" size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Live Support</h3>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                <span className="text-xs text-white/90">Online</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-white" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {messages?.map((msg) => (
            <div
              key={msg?.id}
              className={`flex ${msg?.isAgent ? 'justify-start' : 'justify-end'}`}
            >
              <div className={`max-w-[80%] ${
                msg?.isAgent
                  ? isDarkMode
                    ? 'bg-[#0f1410] border border-[#1a2820]'
                    : 'bg-gray-100 border border-gray-200'
                  : isDarkMode
                    ? 'bg-[#10b981]/10 border border-[#10b981]/20'
                    : 'bg-[#107570]/10 border border-[#107570]/20'
              } rounded-lg p-3`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-foreground">
                    {msg?.sender}
                  </span>
                  {msg?.isBot && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      isDarkMode ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-100 text-blue-700'
                    }`}>
                      Bot
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground mb-1">
                  {msg?.message}
                </p>
                <p className="text-xs text-muted-foreground">
                  {msg?.timestamp}
                </p>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className={`px-4 py-3 rounded-lg ${
                isDarkMode ? 'bg-[#0f1410]' : 'bg-gray-100'
              }`}>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Replies */}
        {messages?.length === 1 && (
          <div className={`px-4 py-3 border-t ${
            isDarkMode ? 'border-[#1a2820]' : 'border-gray-200'
          }`}>
            <p className="text-xs text-muted-foreground mb-2">Quick replies:</p>
            <div className="flex flex-wrap gap-2">
              {quickReplies?.map((reply, index) => (
                <button
                  key={index}
                  onClick={() => setMessage(reply)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    isDarkMode
                      ? 'border-[#1a2820] hover:border-[#10b981]/50 text-foreground'
                      : 'border-gray-300 hover:border-[#107570] text-gray-700'
                  }`}
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSendMessage} className={`p-4 border-t ${
          isDarkMode ? 'border-[#1a2820]' : 'border-gray-200'
        }`}>
          <div className="flex gap-2">
            <Input
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              className="flex-1"
            />
            <Button 
              type="submit" 
              variant="default" 
              size="icon"
              disabled={!message?.trim()}
            >
              <Icon name="Send" size={18} />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LiveChatWidget;